import React, { useEffect, useRef, useState } from 'react';
import { Moon, Sun, Check } from 'lucide-react';
import { useTheme, ColorAccent, ThemeMode } from '../contexts/ThemeContext';

/* ─── Palette definitions ──────────────────────────────────────────────── */
interface AccentDef {
  id: ColorAccent;
  label: string;
  darkGrad: string;
  lightGrad: string;
  darkRing: string;
  lightRing: string;
}

const ACCENTS: AccentDef[] = [
  {
    id: 'cyan',    label: 'Cyan',
    darkGrad:  'linear-gradient(135deg,#22d3ee,#0891b2)',
    lightGrad: 'linear-gradient(135deg,#0891b2,#0e7490)',
    darkRing:  'rgba(34,211,238,0.55)',
    lightRing: 'rgba(8,145,178,0.5)',
  },
  {
    id: 'violet',  label: 'Violet',
    darkGrad:  'linear-gradient(135deg,#a78bfa,#7c3aed)',
    lightGrad: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
    darkRing:  'rgba(167,139,250,0.55)',
    lightRing: 'rgba(124,58,237,0.5)',
  },
  {
    id: 'rose',    label: 'Rose',
    darkGrad:  'linear-gradient(135deg,#fb7185,#e11d48)',
    lightGrad: 'linear-gradient(135deg,#e11d48,#be123c)',
    darkRing:  'rgba(251,113,133,0.55)',
    lightRing: 'rgba(225,29,72,0.5)',
  },
  {
    id: 'amber',   label: 'Amber',
    darkGrad:  'linear-gradient(135deg,#fbbf24,#d97706)',
    lightGrad: 'linear-gradient(135deg,#d97706,#b45309)',
    darkRing:  'rgba(251,191,36,0.55)',
    lightRing: 'rgba(217,119,6,0.5)',
  },
  {
    id: 'emerald', label: 'Emerald',
    darkGrad:  'linear-gradient(135deg,#34d399,#059669)',
    lightGrad: 'linear-gradient(135deg,#059669,#047857)',
    darkRing:  'rgba(52,211,153,0.55)',
    lightRing: 'rgba(5,150,105,0.5)',
  },
  {
    id: 'indigo',  label: 'Indigo',
    darkGrad:  'linear-gradient(135deg,#818cf8,#4f46e5)',
    lightGrad: 'linear-gradient(135deg,#4f46e5,#3730a3)',
    darkRing:  'rgba(129,140,248,0.55)',
    lightRing: 'rgba(79,70,229,0.5)',
  },
];

const MODE_OPTIONS: { id: ThemeMode; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { id: 'dark',  label: 'Dark',  Icon: ({ className }) => <Moon className={className} /> },
  { id: 'light', label: 'Light', Icon: ({ className }) => <Sun  className={className} /> },
];

export default function ThemePicker() {
  const { mode, accent, setMode, setAccent } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isLight = mode === 'light';

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open]);

  const currentAccent = ACCENTS.find((a) => a.id === accent) ?? ACCENTS[0];
  const swatchGrad = isLight ? currentAccent.lightGrad : currentAccent.darkGrad;

  return (
    <div ref={wrapRef} className="relative">

      {/* ── Trigger button ────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Theme settings"
        className={[
          'theme-picker-trigger flex items-center gap-1.5 rounded-xl px-2.5 py-1.5',
          'text-xs font-semibold border select-none',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          isLight
            ? `bg-white border-slate-300 text-slate-700 shadow-sm ${open ? 'border-slate-400 shadow-md' : 'hover:border-slate-400 hover:shadow-md'}`
            : `bg-white/[0.05] border-white/[0.10] text-tech-text-muted ${open ? 'bg-white/[0.09] border-white/[0.16] text-tech-text' : 'hover:bg-white/[0.08] hover:border-white/[0.15] hover:text-tech-text'}`,
        ].join(' ')}
      >
        <span
          className="w-3 h-3 rounded-full shrink-0"
          style={{
            background: swatchGrad,
            boxShadow: `0 0 0 1.5px ${isLight ? currentAccent.lightRing : currentAccent.darkRing}`,
          }}
          aria-hidden
        />
        <span className="hidden sm:flex items-center gap-1">
          {isLight ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          <span className="hidden md:inline leading-none capitalize">{mode}</span>
        </span>
        <svg
          className={`w-3 h-3 transition-transform duration-200 opacity-50 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden
        >
          <polyline points="2 4 6 8 10 4" />
        </svg>
      </button>

      {/* ── Dropdown panel ────────────────────────────────────────────── */}
      {open && (
        <div
          className={[
            'absolute right-0 top-full mt-2 z-[200] w-[272px] rounded-2xl p-4',
            'animate-slide-down origin-top-right',
            isLight
              ? 'bg-white border border-slate-200/80 shadow-[0_12px_48px_rgba(0,0,0,0.20),0_2px_8px_rgba(0,0,0,0.10),0_0_0_1px_rgba(0,0,0,0.04)]'
              : 'bg-[#0e1117]/96 border border-white/[0.09] shadow-[0_12px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-2xl',
          ].join(' ')}
          role="dialog"
          aria-label="Theme options"
        >
          {/* header */}
          <div className="flex items-center justify-between mb-4">
            <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${isLight ? 'text-slate-500' : 'text-tech-text-muted'}`}>
              Appearance
            </span>
            <button
              type="button" onClick={() => setOpen(false)} aria-label="Close"
              className={`w-6 h-6 flex items-center justify-center rounded-lg transition-colors
                ${isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-tech-text-muted hover:text-tech-text hover:bg-white/[0.07]'}`}
            >
              <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="1" y1="1" x2="11" y2="11" /><line x1="11" y1="1" x2="1" y2="11" />
              </svg>
            </button>
          </div>

          {/* ── Mode segmented control ───────────────────────────────── */}
          <div className={`flex rounded-xl p-1 gap-1 mb-5 ${isLight ? 'bg-slate-100 border border-slate-200' : 'bg-white/[0.04]'}`}>
            {MODE_OPTIONS.map(({ id, label, Icon }) => {
              const active = mode === id;
              return (
                <button
                  key={id} type="button" onClick={() => setMode(id)} aria-pressed={active}
                  className={[
                    'flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-3',
                    'text-xs font-bold transition-all duration-200 select-none',
                    active
                      ? isLight
                        ? 'bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.16),0_0_0_1px_rgba(0,0,0,0.07)]'
                        : 'bg-white/[0.11] text-white shadow-[0_1px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.07)]'
                      : isLight
                        ? 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
                        : 'text-slate-500 hover:text-slate-300',
                  ].join(' ')}
                >
                  <Icon className="w-4 h-4" />{label}
                </button>
              );
            })}
          </div>

          {/* ── Accent colour heading ─────────────────────────────────── */}
          <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isLight ? 'text-slate-500' : 'text-tech-text-muted'}`}>
            Accent color
          </p>

          {/* ── Colour swatches grid ──────────────────────────────────── */}
          <div className="grid grid-cols-3 gap-1.5">
            {ACCENTS.map((a) => {
              const active = accent === a.id;
              const grad = isLight ? a.lightGrad : a.darkGrad;
              const ring = isLight ? a.lightRing : a.darkRing;
              return (
                <button
                  key={a.id} type="button" onClick={() => setAccent(a.id)}
                  aria-pressed={active} title={a.label}
                  className={[
                    'flex items-center gap-2 rounded-xl px-3 py-2.5 text-left',
                    'text-xs font-semibold transition-all duration-150 select-none',
                    active
                      ? isLight
                        ? 'bg-slate-100 text-slate-900 ring-1 ring-slate-300'
                        : 'bg-white/[0.10] text-white ring-1 ring-white/[0.15]'
                      : isLight
                        ? 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        : 'text-slate-400 hover:bg-white/[0.05] hover:text-slate-200',
                  ].join(' ')}
                >
                  <span className="relative shrink-0">
                    <span
                      className="block w-5 h-5 rounded-full"
                      style={{ background: grad, boxShadow: active ? `0 0 0 2.5px white, 0 0 0 4px ${ring}` : 'none' }}
                    />
                    {active && (
                      <Check
                        className="absolute inset-0 m-auto w-3 h-3 text-white"
                        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
                      />
                    )}
                  </span>
                  <span className="truncate leading-none">{a.label}</span>
                </button>
              );
            })}
          </div>

          {/* ── Preview strip ─────────────────────────────────────────── */}
          <div className={`mt-4 pt-3 flex items-center gap-2 border-t ${isLight ? 'border-slate-200' : 'border-white/[0.07]'}`}>
            <div className="h-1.5 flex-1 rounded-full" style={{ background: swatchGrad }} />
            <span className={`text-[10px] font-semibold capitalize ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
              {currentAccent.label} · {mode}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
